'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { Loader2, Clock, Calendar, CheckCircle2, Coffee, LogOut, Hourglass, CalendarCheck, CalendarDays, List, XCircle } from 'lucide-react'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { Input } from '@/components/ui/input'
import { formatTime, cn } from '@/lib/utils'
import { todayIST, formatISTDate, istHourMinute } from '@/lib/date-utils'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { SectionHeader } from '@/components/ui/section-header'
import { PageShell } from '@/components/ui/page-shell'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { AttendanceCalendar, type CalendarDay } from '@/components/ui/attendance-calendar'
import { classifyDay } from '@/lib/attendance-classify'

interface BreakEntry {
  start: string | null
  end: string | null
}

interface AttendanceRecord {
  date: string
  checkIn: string | null
  checkOut: string | null
  status: string
  workMode: string
  notes: string | null
  breaks?: BreakEntry[]
}

interface EmployeeAttendanceProps {
  onRefresh: () => void
  userId: string
  onNavigate?: (tab: string) => void
}

function timeToMinutes(t: string | null): number {
  if (!t) return 0
  if (t.includes('T')) {
    const d = new Date(t)
    return d.getHours() * 60 + d.getMinutes()
  }
  const [h, m] = t.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function totalBreakMinutes(breaks?: BreakEntry[]): number {
  if (!Array.isArray(breaks)) return 0
  let t = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      const d = timeToMinutes(b.end) - timeToMinutes(b.start)
      if (d > 0) t += d
    }
  }
  return t
}

function workedMinutes(record: AttendanceRecord | null, nowMin: number): number {
  if (!record?.checkIn) return 0
  const start = timeToMinutes(record.checkIn)
  const end = record.checkOut ? timeToMinutes(record.checkOut) : nowMin
  const breakM = totalBreakMinutes(record.breaks)
  return Math.max(0, end - start - breakM)
}

function fmtHM(mins: number) {
  if (mins <= 0) return '0h 0m'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h}h ${m}m`
}

export function EmployeeAttendance({ onRefresh, userId, onNavigate }: EmployeeAttendanceProps) {
  const [todayAttendance, setTodayAttendance] = useState<AttendanceRecord | null>(null)
  const [history, setHistory] = useState<AttendanceRecord[]>([])
  const [limit, setLimit] = useState(10)
  const [leaveDates, setLeaveDates] = useState<Set<string>>(new Set())
  const [holidayDates, setHolidayDates] = useState<Set<string>>(new Set())
  const [historyView, setHistoryView] = useState<'list' | 'calendar'>('calendar')
  const [calendarMonth, setCalendarMonth] = useState(() => todayIST().slice(0, 7))
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [workMode, setWorkMode] = useState('OFFICE')
  const [confirmCheckout, setConfirmCheckout] = useState(false)
  const [workUpdateDone, setWorkUpdateDone] = useState(false)

  const [nowTick, setNowTick] = useState(Date.now())
  const [pendingCheckinRequest, setPendingCheckinRequest] = useState<boolean>(false)
  const [officeCheckInTime, setOfficeCheckInTime] = useState(() => {
    const { hour, minute } = istHourMinute()
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  })
  // Whether the user has hand-picked a check-in time. Until they do, the field
  // must track the live clock — otherwise a value frozen at page-load drifts into
  // the past, and the backend silently turns a normal check-in into a *backdated
  // approval request* (it flags check-ins more than 11 min behind "now").
  const [timeEdited, setTimeEdited] = useState(false)
  const [scheduledCheckInTime, setScheduledCheckInTime] = useState('10:00')

  const today = todayIST()

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000)
    return () => clearInterval(t)
  }, [])

  // Keep the office check-in time pinned to "now" (IST) until the user edits it or
  // has already checked in. Driven by nowTick so it stays fresh on an idle page.
  useEffect(() => {
    if (timeEdited || todayAttendance?.checkIn) return
    const { hour, minute } = istHourMinute()
    setOfficeCheckInTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
  }, [nowTick, timeEdited, todayAttendance?.checkIn])

  // Bounded window instead of the user's entire history. Firestore bills per
  // document returned, so an unbounded read here — repeated on a timer — was what
  // exhausted the project's daily read quota. Covers the month the calendar is
  // showing plus a recent lookback for the week/streak tiles.
  const fetchWindow = useMemo(() => {
    const [y, m] = calendarMonth.split('-').map(Number)
    const monthStart = `${calendarMonth}-01`
    const monthEnd = `${calendarMonth}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
    const lookback = new Date()
    lookback.setDate(lookback.getDate() - 45)
    const recentStart = formatISTDate(lookback)
    return {
      startDate: monthStart < recentStart ? monthStart : recentStart,
      endDate: monthEnd > today ? monthEnd : today,
    }
  }, [calendarMonth, today])

  const fetchAttendance = useCallback(async () => {
    if (!userId) return
    try {
      // Settled independently: a failing check-in-requests endpoint must not
      // blank the attendance history.
      const asJson = async (p: Promise<Response>) => (await p).json()
      const params = new URLSearchParams(fetchWindow).toString()
      const [attSettled, checkinSettled] = await Promise.allSettled([
        asJson(apiFetch(`/api/attendance?${params}`)),
        asJson(apiFetch('/api/checkin-requests'))
      ])

      if (attSettled.status === 'rejected') {
        throw attSettled.reason
      }
      if (checkinSettled.status === 'rejected') {
        console.error('Error fetching check-in requests:', checkinSettled.reason)
      }

      const data = attSettled.value || {}
      const checkinData = checkinSettled.status === 'fulfilled' ? (checkinSettled.value || {}) : {}

      const records: AttendanceRecord[] = (data.attendance || []) as AttendanceRecord[]
      // API already sorts by date descending.
      setTodayAttendance(records.find(r => r.date === today) || null)
      setHistory(records)

      const todayCheckinReq = (checkinData.requests || []).find((r: any) => r.date === today && r.status === 'PENDING')
      setPendingCheckinRequest(!!todayCheckinReq)
    } catch (e: any) {
      console.error(e)
      toast.error(e.message || 'Failed to fetch attendance data')
    } finally {
      setLoading(false)
    }
  }, [userId, today, fetchWindow])

  /**
   * The live hero card only needs *today*. Re-reading the whole window on a timer
   * cost one Firestore read per historical day, every tick; this costs one.
   */
  const refreshToday = useCallback(async () => {
    if (!userId) return
    try {
      const res = await apiFetch(`/api/attendance?date=${today}`)
      const data = await res.json()
      const rec = ((data.attendance || []) as AttendanceRecord[]).find(r => r.date === today) || null
      setTodayAttendance(rec)
      setHistory(prev => {
        const rest = prev.filter(r => r.date !== today)
        return rec ? [rec, ...rest] : rest
      })
    } catch (e) {
      console.error('Error refreshing today\'s attendance:', e)
    }
  }, [userId, today])

  useEffect(() => {
    fetchAttendance()
  }, [fetchAttendance])

  useEffect(() => {
    const t = setInterval(refreshToday, 60000)
    return () => clearInterval(t)
  }, [refreshToday])

  useEffect(() => {
    apiFetch('/api/config').then(res => res.json()).then(data => {
      if (data?.config?.officeCheckInTime) setScheduledCheckInTime(data.config.officeCheckInTime)
    }).catch(console.error)
  }, [])

  useEffect(() => {
    async function fetchLeaves() {
      try {
        const res = await apiFetch('/api/leave')
        const data = await res.json()
        if (data.leaves) {
          const dates = new Set<string>()
          for (const leave of data.leaves) {
            if (leave.status !== 'APPROVED') continue
            const start = new Date(leave.startDate + 'T00:00:00')
            const end = new Date(leave.endDate + 'T00:00:00')
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              dates.add(formatISTDate(d))
            }
          }
          setLeaveDates(dates)
        }
      } catch (e) { console.error(e) }
    }
    fetchLeaves()
  }, [])

  // Holidays feed the calendar grid so a company holiday isn't painted as an absence.
  useEffect(() => {
    apiFetch('/api/holidays')
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data?.holidays)) return
        setHolidayDates(new Set(data.holidays.map((h: any) => h?.date).filter(Boolean)))
      })
      .catch(console.error)
  }, [])

  // Checkout is gated on today's work update — the server enforces it too; this
  // only keeps the button from promising a click the API will refuse. Re-runs when
  // the tab remounts, so submitting the log in the Daily Work tab is picked up on
  // return. Keyed on the IST date, matching the dailyWork doc id scheme.
  useEffect(() => {
    apiFetch(`/api/daily-work?date=${today}`)
      .then(res => res.json())
      .then(data => setWorkUpdateDone((data.dailyWorks || []).length > 0))
      .catch(e => console.error("Error fetching today's work update:", e))
  }, [today])



  async function postAction(action: string, body: any = {}) {
    setBusy(true)
    try {
      const res = await apiFetch('/api/attendance', {
        method: 'POST',
        body: JSON.stringify({ action, ...body }),
      })
      const data = await res.json()
      // Surface backend errors instead of swallowing them — without this, a 400
      // (e.g. "End your break before checking out") or a 500 was silently ignored,
      // a success toast fired, and nothing was written. That made checkout / breaks
      // look broken ("attendance not updating", "work description not saved").
      if (!res.ok) throw new Error(data?.error || `Failed: ${action}`)
      return data
    } finally {
      setBusy(false)
    }
  }

  const handleCheckIn = async () => {
    try {
      const data = await postAction('checkin', { workMode, time: workMode === 'OFFICE' ? officeCheckInTime : undefined })
      if (data.pending) {
        setPendingCheckinRequest(true)
        toast.success('Check-in request submitted for approval')
        return
      }
      await fetchAttendance()
      onRefresh()
      toast.success('Checked in')
    } catch (e: any) { toast.error(e.message) }
  }

  // Flip the button straight off the response instead of waiting on a refetch —
  // a slow or failing reload made Start/End Break look like it hadn't worked.
  const applyBreakResponse = (data: any) => {
    if (!Array.isArray(data?.breaks)) return
    setTodayAttendance(prev => (prev ? { ...prev, breaks: data.breaks } : prev))
  }

  const handleBreakStart = async () => {
    try {
      applyBreakResponse(await postAction('breakstart'))
      toast.success('Break started')
      await refreshToday()
    } catch (e: any) { toast.error(e.message) }
  }

  const handleBreakEnd = async () => {
    try {
      applyBreakResponse(await postAction('breakend'))
      toast.success('Break ended')
      await refreshToday()
    } catch (e: any) { toast.error(e.message) }
  }

  // The work log itself is recorded in the Daily Work tab (and required by the
  // API before checkout) — this only closes the day, so it must not ask for the
  // log again. Confirming completes the checkout in one step.
  const handleCheckOut = async () => {
    setConfirmCheckout(false)
    try {
      await postAction('checkout')
      toast.success('Checked out')
      await fetchAttendance()
      onRefresh()
    } catch (e: any) { toast.error(e.message) }
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }

  const getDisplayStatus = (record: AttendanceRecord) => {
    if (record.checkIn) return 'PRESENT'
    if (leaveDates.has(record.date)) return 'LEAVE'
    return 'ABSENT'
  }

  const calendarDays = useMemo(() => {
    const byDate: Record<string, AttendanceRecord> = {}
    for (const r of history) byDate[r.date] = r
    const [y, m] = calendarMonth.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()
    const out: Record<string, CalendarDay> = {}
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${calendarMonth}-${String(d).padStart(2, '0')}`
      const rec = byDate[date]
      const cls = classifyDay({
        date,
        record: rec?.checkIn
          ? { checkIn: rec.checkIn, checkOut: rec.checkOut, workMode: rec.workMode, breaks: rec.breaks }
          : null,
        leaveDates,
        wfhDates: new Set<string>(),
        holidayDates,
        statusOverride: rec?.status ?? null,
      })
      // Days still ahead aren't absences — leave them unmapped so the grid
      // renders them as future placeholders.
      if (date > today && cls.status === 'ABSENT') continue
      out[date] = {
        date,
        status: cls.status,
        hours: cls.hours,
        checkIn: cls.checkIn,
        checkOut: cls.checkOut,
        workMode: cls.workMode,
        incomplete: cls.incomplete,
      }
    }
    return out
  }, [history, calendarMonth, leaveDates, holidayDates, today])

  const todayFormatted = (() => {
    const d = new Date()
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' })
    return `${weekday}, ${day}/${month}/${year}`
  })()

  const breaks = todayAttendance?.breaks || []
  const openBreak = breaks.find(b => b.start && !b.end)
  const breakMin = totalBreakMinutes(breaks)
  const isCheckedIn = !!todayAttendance?.checkIn
  const isCheckedOut = !!todayAttendance?.checkOut

  // "Now" must be IST wall-clock, not the browser's local zone — check-in/out are
  // stored as IST HH:mm, so a non-IST browser would otherwise miscount live hours
  // and show the wrong time under the "IST" label. Recomputed each render; nowTick
  // forces a render every 30s. (nowTick is referenced so this stays reactive.)
  void nowTick
  const { hour: istHour, minute: istMinute } = istHourMinute()
  const nowMin = istHour * 60 + istMinute
  const workedToday = workedMinutes(todayAttendance, nowMin)

  // This week worked minutes (Mon..today)
  const dayOfWeek = (new Date().getDay() + 6) % 7 // Mon=0
  const weekStart = new Date()
  weekStart.setDate(weekStart.getDate() - dayOfWeek)
  const weekStartStr = formatISTDate(weekStart)
  const weekMinutes = history
    .filter(r => r.date >= weekStartStr)
    .reduce((sum, r) => sum + workedMinutes(r, nowMin), 0)

  // Present streak (consecutive recent PRESENT days excluding leave/weekends)
  const streak = (() => {
    let s = 0
    for (const r of history) {
      if (getDisplayStatus(r) === 'PRESENT') s++
      else break
    }
    return s
  })()

  // Total absent days for the displayed calendar month. Derived from the same
  // classification the calendar grid uses, so an HR status correction (marking
  // a day Leave or Present) immediately moves this count.
  const absentDays = useMemo(
    () => Object.values(calendarDays).filter(d => d.status === 'ABSENT').length,
    [calendarDays]
  )

  const nowClock = `${istHour % 12 || 12}:${String(istMinute).padStart(2, '0')} ${istHour >= 12 ? 'PM' : 'AM'}`

  return (
    <PageShell>
      <SectionHeader
        icon={CalendarCheck}
        title="Attendance"
        description={todayFormatted}
      />

      {loading ? (
        <TabLoader />
      ) : (
        <>
      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <KpiTile label="Hours Today" value={fmtHM(workedToday)} icon={Clock} tone="orange" hint={isCheckedIn ? (isCheckedOut ? 'Day complete' : 'Live') : 'Not started'} />
        <KpiTile label="This Week" value={fmtHM(weekMinutes)} icon={Calendar} tone="blue" hint="Mon → today" />
        <KpiTile label="Absent Days" value={absentDays} icon={XCircle} tone="red" hint="Working days with no check-in or leave, this month" />
        <KpiTile label="Present Streak" value={streak} icon={CheckCircle2} tone="green" hint={streak === 1 ? 'day' : 'days'} />
      </div>

      {/* Hero — today */}
      <GlassCard tone={isCheckedOut ? 'green' : isCheckedIn ? 'orange' : 'amber'}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill tone={statusTone(isCheckedIn ? 'PRESENT' : leaveDates.has(today) ? 'LEAVE' : 'ABSENT')}>
                {isCheckedIn ? (isCheckedOut ? 'COMPLETED' : 'PRESENT') : leaveDates.has(today) ? 'LEAVE' : 'NOT CHECKED IN'}
              </StatusPill>
              <StatusPill tone="blue">{todayAttendance?.workMode || workMode}</StatusPill>
            </div>
            <div className="flex items-baseline gap-3">
              <p className="text-3xl md:text-4xl font-semibold text-neutral-900 dark:text-white tabular-nums tracking-tight">{nowClock}</p>
              <p className="text-sm text-neutral-400 dark:text-white/40">IST</p>
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Check In</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{formatTime(todayAttendance?.checkIn ?? null) || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Check Out</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{formatTime(todayAttendance?.checkOut ?? null) || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Breaks</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{breakMin}m</p>
              </div>
            </div>
          </div>

          <div className="w-full md:w-72 space-y-3">
            {pendingCheckinRequest ? (
              <div className="flex items-center justify-center gap-2 rounded-xl bg-amber-500/10 ring-1 ring-inset ring-amber-500/20 px-4 py-4 text-amber-700 dark:text-amber-300 w-full">
                <Hourglass className="h-5 w-5 animate-pulse" />
                <span className="font-medium text-sm">Check-in Request Pending Approval</span>
              </div>
            ) : !isCheckedIn ? (
              <>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-white/50">Work mode</label>
                  <Select value={workMode} onValueChange={setWorkMode} disabled={isCheckedIn}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OFFICE">Office</SelectItem>
                      <SelectItem value="WFH">Work From Home</SelectItem>
                      <SelectItem value="HYBRID">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {workMode === 'OFFICE' && (
                  <div className="space-y-1.5">
                    <label className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-white/50">Office check-in time</label>
                    <Input type="time" value={officeCheckInTime} onChange={e => { setOfficeCheckInTime(e.target.value); setTimeEdited(true) }} />
                    <p className="text-[11px] text-neutral-400 dark:text-white/40">Up to 11 minutes backdated from the current time is allowed; earlier check-in times require HR/Admin approval.</p>
                  </div>
                )}
                <Button
                  onClick={handleCheckIn}
                  disabled={busy}
                  className={cn(PRIMARY_BTN, 'w-full h-12 text-base font-semibold')}
                >
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Clock className="mr-2 h-5 w-5" />}
                  Check In Now
                </Button>
              </>
            ) : isCheckedOut ? (
              <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/20 px-4 py-4 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Day completed</span>
              </div>
            ) : openBreak ? (
              <Button
                onClick={handleBreakEnd}
                disabled={busy}
                className="w-full h-12 text-base bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white shadow-lg shadow-amber-500/20"
              >
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Coffee className="mr-2 h-5 w-5" />}
                End Break
              </Button>
            ) : (
              <div className="space-y-2">
                <Button onClick={handleBreakStart} disabled={busy} variant="outline" className="w-full border-neutral-200 dark:border-white/15 bg-neutral-50 dark:bg-white/5 text-neutral-900 dark:text-white/90 hover:bg-neutral-100 dark:hover:bg-white/10">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Coffee className="mr-2 h-4 w-4" />}
                  Start Break
                </Button>
                {/* Disabled until today's work log exists — the API refuses the
                    checkout otherwise, and the old description dialog here asked
                    for the log again while never actually satisfying that gate. */}
                <Button
                  onClick={() => setConfirmCheckout(true)}
                  disabled={busy || !workUpdateDone}
                  className={cn(PRIMARY_BTN, 'w-full h-11')}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Check Out
                </Button>
                {!workUpdateDone && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                      Submit your Today&apos;s Work Update before checking out.
                    </p>
                    {onNavigate && (
                      <button
                        onClick={() => onNavigate('daily-work')}
                        className="shrink-0 rounded-md text-[10px] font-semibold text-orange-600 underline-offset-2 hover:underline dark:text-orange-300"
                      >
                        Log work
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {breaks.length > 0 && (
          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-white/10 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Break log</p>
            <div className="flex flex-wrap gap-2">
              {breaks.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 ring-1 ring-inset ring-amber-500/20 px-2.5 py-1 text-[11px] text-amber-700 dark:text-amber-200">
                  <Coffee className="h-3 w-3" />
                  {formatTime(b.start)} – {b.end ? formatTime(b.end) : <span className="text-amber-600 dark:text-amber-300">live</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {/* History */}
      <GlassCard>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">
              {historyView === 'calendar' ? 'Attendance calendar' : 'Recent attendance'}
            </h3>
            <p className="text-[11px] text-neutral-400 dark:text-white/40">
              {historyView === 'calendar' ? 'Day-by-day status for the month' : 'Most recent days first'}
            </p>
          </div>
          <div className="inline-flex shrink-0 rounded-xl bg-neutral-100 p-0.5 ring-1 ring-inset ring-neutral-200 dark:bg-white/5 dark:ring-white/10">
            {([['calendar', 'Calendar', CalendarDays], ['list', 'List', List]] as const).map(([v, label, Icon]) => (
              <button
                key={v}
                type="button"
                onClick={() => setHistoryView(v)}
                aria-pressed={historyView === v}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-xs font-medium transition-colors',
                  historyView === v
                    ? 'bg-white text-neutral-900 shadow-sm dark:bg-white/10 dark:text-white'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-white/50 dark:hover:text-white/80'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
        {historyView === 'calendar' ? (
          <AttendanceCalendar
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            days={calendarDays}
          />
        ) : history.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No attendance records yet"
            description="Your check-ins will appear here once you start logging your days."
          />
        ) : (
          <div className="space-y-2.5">
            {history.slice(0, limit).map(record => {
              const status = getDisplayStatus(record)
              const bm = totalBreakMinutes(record.breaks)
              return (
                <div
                  key={record.date}
                  className="rounded-xl p-3 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 hover:ring-neutral-300 dark:ring-white/15 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <Calendar className="h-4 w-4 text-neutral-400 dark:text-white/40" />
                      <span className="text-sm text-neutral-800 dark:text-white/80 tabular-nums">{formatDate(record.date)}</span>
                      <StatusPill tone={statusTone(status)}>{status}</StatusPill>
                    </div>
                    {status === 'PRESENT' && (
                      <div className="space-y-1 mt-1 sm:mt-0 text-right">
                        <div className="flex items-center gap-2 flex-wrap text-[11px] text-neutral-500 dark:text-white/50 justify-end">
                          <span>In <span className="text-neutral-800 dark:text-white/80 tabular-nums">{formatTime(record.checkIn)}</span></span>
                          <span>Out <span className="text-neutral-800 dark:text-white/80 tabular-nums">{formatTime(record.checkOut) || '—'}</span></span>
                          {bm > 0 && <StatusPill tone="amber">Break {bm}m</StatusPill>}
                          <StatusPill tone="blue">{record.workMode}</StatusPill>
                        </div>
                        {record.notes && (
                          <div className="text-xs text-neutral-600 dark:text-white/60 bg-neutral-100/50 dark:bg-white/[0.01] rounded-lg p-2 mt-1 text-left">
                            <span className="font-medium text-neutral-700 dark:text-white/70">Work Done: </span>{record.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            {history.length > limit && (
              <div className="flex justify-center pt-2">
                <Button variant="ghost" className="text-orange-500 hover:text-orange-600 text-xs font-semibold" onClick={() => setLimit(prev => prev + 10)}>
                  Show More
                </Button>
              </div>
            )}
          </div>
        )}
      </GlassCard>
        </>
      )}

      <AlertDialog open={confirmCheckout} onOpenChange={setConfirmCheckout}>
        <AlertDialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-neutral-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900 dark:text-white/90">
              Check out for the day?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500 dark:text-white/60">
              Your check-out time will be recorded as the current time. You won&apos;t be able to check back in today.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-200 dark:border-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCheckOut} disabled={busy} className={PRIMARY_BTN}>
              Confirm Check Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </PageShell>
  )
}
